import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { ClicksDataService, ClicksColumnsService } from '../../services';
import { DialogClickFormComponent } from './click-form/click-form.component';
import { DialogClickDetailsComponent } from './click-details/click-details.component';
import { Click, ClicksResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-clicks',
  templateUrl: './clicks.component.html',
  styleUrls: ['./clicks.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class ClicksComponent implements OnInit, OnDestroy {

  private clickAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private clickEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private clickDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public clickToDelete: Click = {};
  public clicks: Click[] = [];
  public click: Click = {};
  public selectedClicks: Click[] = [];
  public submitted: boolean = false;
  public parameters: {[param: string]: string | string[]} = {offset: '0', limit: '100'};
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language)?localStorage.language:'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private clicksDataService: ClicksDataService,
    private clicksColumnsService: ClicksColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('ClicksComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Clicks - Management');
    this.primengConfig.ripple = true;
    this.getClicks();
  }

  ngOnDestroy(): void {
    console.log('ClicksComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getClicks(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.clicksDataService.getClicks(this.parameters).subscribe((response: ClicksResult|any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.clicks = response.results;
        } else {
          this.clicks = this.clicks.concat(response.results);
        }
        if (response.inlineCount > this.clicks.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getClicks();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {;
      this.isLoading = false;
      this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLICKSNOTLOADED')});
    });
  }

  importClicks(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.clicksDataService.addClicks(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CLICKSCREATED')});
                if (proceed === j) {
                  this.getClicks();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLICKSNOTCREATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLICKSNOTCREATED') + ' ' + msg});
            });
          } else {
            this.clicksDataService.updateClicks(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CLICKSUPDATED')});
                if (proceed === j) {
                  this.getClicks();
                }
              } else {
                this.messageService.add({severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLICKSNOTUPDATED')});
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CLICKSNOTCREATED') + ' ' + msg});
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.clicksColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
      return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
      // restore original order
      this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
      this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('clicks.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedClicks() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Clicks?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.clicksDataService.deleteClicks(this.selectedClicks).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({severity:'success', summary: 'Successful', detail: 'Clicks Deleted', life: 3000});
            this.getClicks();
          } else {
            this.messageService.clear();
            this.messageService.add({severity: 'error', summary: 'Error', detail: 'Clicks not deleted'});
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({severity: 'error', summary: 'Error', detail: 'Clicks not deleted'});
        });
        this.selectedClicks = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.clicks, this.columns, 'Clicks');
  }

  exportExcel() {
    ToolsService.exportExcel(this.clicks, this.columns, 'Clicks');
  }

  exportCsv() {
    ToolsService.exportCsv(this.clicks, this.columns, 'Clicks');
  }

  refresh() {
    this.clicksDataService.clearCache();
    this.getClicks();
  }

  clickAdd(): void {
    if (!this.navigation.getParamValue('clicks.addInDialog', true)) {
      this.router.navigate(['/clicks/add']);
      return;
    }
    this.clickAddDialog = this.dialogService.open(DialogClickFormComponent, {
      header: '', // this.translate.instant('NEWCLICK'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        click: {id: null},
        visits: this.clicksColumnsService.visits,
        banners: this.clicksColumnsService.banners,
        users: this.clicksColumnsService.users,
      },
    });

    this.clickAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'info', summary: 'Success', detail: 'Click added'});
        this.getClicks();
      }
    });
  }

  clickEdit(click: Click): void {
    if (!this.navigation.getParamValue('clicks.editInDialog', true)) {
      this.router.navigate(['/clicks/edit/' + click.id]);
      return;
    }
    this.clickEditDialog = this.dialogService.open(DialogClickFormComponent, {
      header: '', // this.translate.instant('EDITCLICK') + ' ' + click.id,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        click: click,
        visits: this.clicksColumnsService.visits,
        banners: this.clicksColumnsService.banners,
        users: this.clicksColumnsService.users,
      },
    });

    this.clickEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({severity: 'success', summary: 'Success', detail: 'Click updated'});
        this.getClicks();
      }
    });
  }

  clickDetails(click: Click): void {
    if (!this.navigation.getParamValue('clicks.detailsInDialog', true)) {
      this.router.navigate(['/clicks/details/' + click.id]);
      return;
    }
    this.clickDetailsDialog = this.dialogService.open(DialogClickDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        click: click,
      }
    });
  }

  clickDelete(click: Click): void {
    this.clickToDelete = click;
    this.messageService.clear();
    this.messageService.add({
      key: 'clickDelete',
      sticky: true,
      severity: 'warn',
      summary: 'Are you sure you want delete ' + click.id + ' ?',
      detail: 'Confirm to proceed'
    });
  }

  onDeleteConfirm(): void {
    this.clicksDataService.deleteClick(this.clickToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'success', summary: 'Success', detail: 'Click deleted'});
      this.getClicks();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Click not deleted'});
    });
  }

  onDeleteReject(): void {
    this.clickToDelete = {};
    this.messageService.clear('clickDelete');
  }

}
